import { IllegalArgumentError } from '../sdk/errors';
import { Duration } from 'luxon';
import { TokenStore } from './token-store';
export const DEFAULT_TOKEN_LIFETIME = Duration.fromObject({ days: 1 });
export const MAX_TOKEN_LIFETIME = Duration.fromObject({ months: 4 });
export class TokenProvider {
    static create(authenticationFacade, dataServiceWalletsApiClientV1, ttl = DEFAULT_TOKEN_LIFETIME, tokenStore = TokenStore.createInMemory(), walletCreation = 'implicit') {
        const defaultTokenProvider = new DefaultTokenProvider(ttl, authenticationFacade.tokenGenerator);
        return new CachedTokenProvider(defaultTokenProvider, tokenStore, walletCreation, authenticationFacade.authenticator.parser, authenticationFacade.authenticator.validator, authenticationFacade.subject(), dataServiceWalletsApiClientV1);
    }
}
export class DefaultTokenProvider extends TokenProvider {
    constructor(ttl, tokenGenerator) {
        if (ttl.toMillis() > MAX_TOKEN_LIFETIME.toMillis()) {
            throw new IllegalArgumentError(`Token TTL ${ttl.toHuman()} must be <= ${MAX_TOKEN_LIFETIME.toHuman()}`);
        }
        super();
        this.ttl = ttl;
        this.tokenGenerator = tokenGenerator;
    }
    get() {
        return this.tokenGenerator.generate(this.ttl);
    }
}
export class CachedTokenProvider extends TokenProvider {
    constructor(delegate, tokenStore, walletCreation, tokenParser, tokenValidator, subject, dataServiceWalletsApiClientV1) {
        super();
        this.delegate = delegate;
        this.tokenStore = tokenStore;
        this.walletCreation = walletCreation;
        this.tokenParser = tokenParser;
        this.tokenValidator = tokenValidator;
        this.subject = subject;
        this.dataServiceWalletsApiClientV1 = dataServiceWalletsApiClientV1;
        this.delegateGetPromises = {};
    }
    async get() {
        const existingToken = this.getCachedToken();
        const subject = this.subject.toString();
        if (existingToken && this.tokenValidator.isValid(existingToken)) {
            return existingToken;
        }
        const existingDelegatePromise = this.delegateGetPromises[subject];
        if (existingDelegatePromise) {
            return existingDelegatePromise;
        }
        const delegatePromise = this.delegate.get().then(async (it) => {
            this.tokenStore.save(this.subject, it.rawValue);
            delete this.delegateGetPromises[subject];
            const wallet = {
                publicKey: this.subject,
            };
            if (this.walletCreation === 'implicit') {
                await this.dataServiceWalletsApiClientV1.upsertWallet(wallet, it);
            }
            return it;
        });
        // delete promise to refetch the token in case of failure
        delegatePromise.catch((it) => {
            delete this.delegateGetPromises[subject];
        });
        this.delegateGetPromises[subject] = delegatePromise;
        return delegatePromise;
    }
    hasValidCachedToken() {
        const cachedToken = this.getCachedToken();
        if (!cachedToken) {
            return false;
        }
        return this.tokenValidator.isValid(cachedToken);
    }
    getCachedToken() {
        const rawToken = this.tokenStore.get(this.subject);
        if (!rawToken) {
            return null;
        }
        try {
            return this.tokenParser.parse(rawToken);
        }
        catch (e) {
            this.tokenStore.delete(this.subject);
            return null;
        }
    }
}
//# sourceMappingURL=token-provider.js.map